import { Injectable } from '@angular/core';
import { ServerApiService } from './server-api.service';
import { Observable } from 'rxjs';
import { PostDO, PostsDataDO } from '../data-objects/PostDO';
import { map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { RecordDO, RecordsDataDO } from '../data-objects/RecordsDataDO';

@Injectable({
  providedIn: 'root',
})
export class PostsService extends ServerApiService {
  public static OFFLINE_ACTIVITIES = 4;
  public static ONLINE_ACTIVITIES = 6;
  public static PRESCHOOL_ACTIVITIES = 14;
  public static PRIMARY_SCHOOL = 270;
  public static GYMNASIUM = 271;
  public static HIGH_SCHOOL = 272;
  public static MOBILE = 273;
  public static DESKTOP = 274;
  public static COLORING_PAGES = 15;
  public static SONGS_PAGES = 455;
  public static REVIEW_PAGE = 371;
  public static CARTOONS_PAGES = 456;
  public static AUDIO_PAGES = 1405;
  public static CRAFTS_PAGES = 1252;
  public static REVIEWS_GENERAL = 371;
  public static REVIEWS_BOOKS = 372;
  public static REVIEWS_OFFLINE_GAMES = 373;
  public static REVIEWS_ONLINE_GAMES = 374;
  public static BLOG_POSTS = 386;
  private allActivities = [
    PostsService.OFFLINE_ACTIVITIES,
    PostsService.ONLINE_ACTIVITIES
  ];
  AIRTABLE_API_KEY = 'keyBFf5jBSFXAWiDB';

  private allReviews = [
    PostsService.REVIEWS_GENERAL,
    PostsService.REVIEWS_BOOKS,
    PostsService.REVIEWS_OFFLINE_GAMES,
    PostsService.REVIEWS_ONLINE_GAMES
  ];

  protected baseUrl = 'https://raduanastase.com/fb-api/wp-json/wp/v2/';

  getActivities(searchTerm: string = '', page: number = 1): Observable<PostsDataDO> {
    const typesOfActivities = this.allActivities.join(',');
    return this.genericGetPosts(typesOfActivities, searchTerm, page, 5);
  }

  getReviews(perPage?: number): Observable<PostsDataDO> {
    const typesOfReviews = this.allReviews.join(',');
    return this.genericGetPosts(typesOfReviews, '', 1, perPage);
  }

  getBlogPosts(perPage?: number): Observable<PostsDataDO> {
    return this.genericGetPosts(`${PostsService.BLOG_POSTS}`, '', 1, perPage);
  }


  getColoringPages(searchTerm: string = '', page: number = 1): Observable<PostsDataDO> {
    return this.genericGetPosts(`${PostsService.COLORING_PAGES}`, searchTerm, page);
  }

  getSongsPages(searchTerm: string = '', page: number = 1): Observable<PostsDataDO> {
    return this.genericGetPosts(`${PostsService.SONGS_PAGES}`, searchTerm, page);
  }

  getCartoonsPages(searchTerm: string = '', page: number = 1): Observable<PostsDataDO> {
    return this.genericGetPosts(`${PostsService.CARTOONS_PAGES}`, searchTerm, page);
  }

  getAudioPages(searchTerm: string = '', page: number = 1): Observable<PostsDataDO> {
    return this.genericGetPosts(`${PostsService.AUDIO_PAGES}`, searchTerm, page);
  }

  getCraftPages(searchTerm: string = '', page: number = 1): Observable<PostsDataDO> {
    return this.genericGetPosts(`${PostsService.CRAFTS_PAGES}`, searchTerm, page);
  }

  getPosts(searchTerm: string = '', page: number = 1): Observable<PostsDataDO> {
    return this.genericGetPosts('', searchTerm, page);
  }

  private genericGetPosts(typesOfPosts: string, search: string = '', page: number = 1, perPage: number = 9): Observable<PostsDataDO> {
    search = search === null ? '' : search;
    return this.runServerGetAction(`posts?page=${page}&per_page=${perPage}${typesOfPosts ? `&categories=${typesOfPosts}` : ''}&search=${search}`)
      .pipe(map((data: HttpResponse<object[]>) => {
        const postsData = new PostsDataDO();
        postsData.buildFromObject({
          posts: data && data.body ? data.body : [],
          pagesCount: data && data.headers ? data.headers.get('X-WP-TotalPages') : 0,
          itemsCount: data && data.headers ? data.headers.get('X-WP-Total') : 0
        });
        return postsData;
      }));
  }

  getPost(id: number): Observable<PostDO> {
    return this.runServerGetAction(`posts/${id}`).pipe(map((postData: HttpResponse<object>) => {
      const post = new PostDO();
      post.buildFromObject(postData.body);
      return post;
    }));
  }

  getPostBySlug(slug: string): Observable<PostDO> {
    return this.runServerGetAction(`posts?slug=${slug}`).pipe(map((postData: HttpResponse<object>) => {
      const post = new PostDO();
      post.buildFromObject(postData.body[0]);
      return post;
    }));
  }

  getPage(slug: string): Observable<PostDO> {
    return this.runServerGetAction(`pages?slug=${slug}`).pipe(map((postData: HttpResponse<object>) => {
      const post = new PostDO();
      post.buildFromObject(postData.body[0]);
      return post;
    }));
  }

  getProjects(): Observable<RecordsDataDO> {
    return this.runServerGetAction(`https://api.airtable.com/v0/appgGQL7g2iw9aGPB/projects?view=gridView&api_key=${this.AIRTABLE_API_KEY}`)
      .pipe(map((postData: HttpResponse<object>) => {
        const recordData = new RecordsDataDO();
        recordData.buildFromObject(postData.body);
        return recordData;
      }));
  }

  updateProjects(records: RecordsDataDO): Observable<RecordsDataDO> {
    return this.runServerPatchAction(
      `https://api.airtable.com/v0/appgGQL7g2iw9aGPB/projects`,
      records,
      [{name: 'Authorization', value: 'Bearer ' + this.AIRTABLE_API_KEY}]
      ).pipe(map((postData: object) => {
        const recordData = new RecordsDataDO();
        recordData.buildFromObject(postData);
        return recordData;
      }));
  }

  updateOneProject(record: RecordDO): Observable<RecordDO> {
    return this.runServerPatchAction(
      `https://api.airtable.com/v0/appgGQL7g2iw9aGPB/projects/${record.id}`,
      {fields: record.fields},
      [{name: 'Authorization', value: 'Bearer ' + this.AIRTABLE_API_KEY}]
    ).pipe(map((postData: object) => {
      const recordDO = new RecordDO();
      recordDO.buildFromObject(postData);
      return recordDO;
    }));
  }
}
